import { useCallback, useState } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  typographyVariants,
} from '@mint-lib/tw-components';
import { TextArea } from '@mint-lib/tw-components/patterns';

import { useInterruptChat } from './helperHooks/useInterruptChat.jsx';
import { useInterruptChatDialog } from './helperHooks/useInterruptChatDialog.jsx';

const CANCEL_OPTIONS = (t: TFunction) => {
  return [
    t('I want to make a different request'),
    t('I am not sure the AI understood my request'),
    t('The chat seemed stuck'),
    t('Other'),
  ];
};

const TEXT_AREA_MAX_LENGTH = 999;

const InterruptChatDialog = ({ chatId }: { chatId: string }) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const [reason, setReason] = useState('');
  const [optionReason, setOptionReason] = useState('');

  const { isOpen, close } = useInterruptChatDialog();
  const { interruptMutation, setIsInterrupted, isInterruptPending } =
    useInterruptChat(chatId);

  const isOtherReason = optionReason === t('Other');

  const handleCloseDialog = useCallback(() => {
    setOptionReason('');
    setReason('');
    close();
  }, [close]);

  const handleOnInterrupt = useCallback(() => {
    interruptMutation(isOtherReason ? reason : optionReason);
    handleCloseDialog();
  }, [
    handleCloseDialog,
    isOtherReason,
    reason,
    optionReason,
    interruptMutation,
  ]);

  const handleOnKeep = useCallback(() => {
    setIsInterrupted?.(false);
    handleCloseDialog();
  }, [handleCloseDialog, setIsInterrupted]);

  return (
    <Dialog open={isOpen} modal>
      <DialogOverlay />
      <DialogRoot size={'xs'} aria-describedby={undefined}>
        <DialogHeader onClose={handleCloseDialog}>
          <DialogTitle className="flex flex-row items-center gap-2">
            {t('Stop this request?')}
          </DialogTitle>
        </DialogHeader>
        <DialogContent className="flex flex-col gap-6">
          <p
            className={typographyVariants({
              variant: 'h6',
            })}
          >
            {t('Help us understand why you are stopping this request')}
          </p>
          <RadioGroup
            value={optionReason}
            onValueChange={(value: string) => {
              setOptionReason(value);
            }}
          >
            {CANCEL_OPTIONS(t).map((item) => (
              <Label
                key={item}
                variant="bodyShort01"
                className="flex cursor-pointer items-center gap-2"
              >
                <RadioGroupItem key={item} value={item} />
                {item}
              </Label>
            ))}
            <TextArea
              className="min-w-82"
              value={reason}
              disabled={!isOtherReason}
              placeholder={t('Add more details')}
              onChange={({ target }) => {
                setReason(target.value);
              }}
              maxLength={TEXT_AREA_MAX_LENGTH}
            />
          </RadioGroup>
        </DialogContent>
        <DialogFooter className="flex flex-row gap-2">
          <Button onClick={handleOnKeep} variant="plain" size="large">
            {t('Keep running')}
          </Button>
          <Button
            color="error"
            onClick={handleOnInterrupt}
            variant="contained"
            size="large"
            disabled={isOtherReason ? reason.length < 3 : !optionReason}
          >
            {isInterruptPending && (
              <CircularLoadingButton size="small" variant="contained" />
            )}
            {t('Stop')}
          </Button>
        </DialogFooter>
      </DialogRoot>
    </Dialog>
  );
};

export { InterruptChatDialog };
